import Container from "@client/content/Containers/container";
import { Form } from "@client/content/controls";
import { getContainerProps, getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { type FormButtonProps, type FormFieldInfo } from "@client/controls/Form/component";
import EmailInput from "@client/controls/Inputs/Email/component";
import { DynamicSelect } from "@client/controls/Inputs/Select/component";
import { usePromise } from "@client/lib/hooks";
import { type OptionItem } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import * as React from "react";
import { ModuleButton } from "../Button";
import { redirect } from "./lib";
import { type SystemRedirect } from "./types";

import "./style.less";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
};

export type ActionProps = {};

type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const [data, setData] = React.useState<SystemRedirect.View.LoginFormData>({
    email: "",
    step: 1,
    tenantId: ""
  });

  // TODO: warning fix. Relevant?
  const [tenants, isLoadingTenants] = usePromise<OptionItem<string>[]>({
    fn: options.getOptions,
    args: ["host", "tenants", { email: data.email }],
    shouldLoad: data.step === 2
  });

  React.useEffect(() => {
    if (tenants?.length === 1) {
      redirect(tenants[0], data.email);
    }
  }, [tenants, data.email]);

  const fields = React.useMemo(() => {
    const fields: FormFieldInfo[] = [
      {
        name: "email",
        label: i18n.email,
        Component: EmailInput,
        isRequired: true,
        defaultValue: data.email
      }
    ];

    if (data.step === 2 && tenants?.length && tenants?.length !== 1) {
      fields.push({
        name: "tenantId",
        label: i18n.websites,
        Component: DynamicSelect,
        isRequired: true,
        componentOpts: {
          options: tenants,
          sort: "asc"
        },
        defaultValue: ""
      });
    }

    return fields;
  }, [props.apiInfo, data.email, data.step, tenants]);

  const next = React.useCallback(() => {
    switch (data.step) {
      case 1: {
        setData({
          ...data,
          step: 2
        });
        break;
      }
      case 2: {
        const tenant = tenants!.find((x) => x.value === data.tenantId);
        tenant && redirect(tenant, data.email);
      }
    }
  }, [data, setData, tenants]);

  const Button = React.useMemo(
    () => (innerProps: FormButtonProps) =>
      (
        <ModuleButton
          {...getRootContainerProps(props)}
          isDisabled={isLoadingTenants}
          onClick={innerProps.ifValidated(next)}
        />
      ),
    [...getRootContainerPropsDep(props), isLoadingTenants, next]
  );

  const onChange = React.useCallback(
    (name: string, value: any) => {
      setData({ ...data, [name]: value, ...(name === "email" ? { step: 1 } : {}) });
    },
    [data, setData]
  );

  return (
    <Container className="wsc_content_host_login_form" {...getContainerProps(props)}>
      <Form {...getRootContainerProps(props)} fields={fields} Button={Button} onChange={onChange} />
    </Container>
  );
};
